import { _, helpers, hooks, mui, React, ts, ui } from '_core';

type SingleReturnHorizonProps = {
  rh: ts.types.returnHorizon.ReturnHorizon;
  setRh: (_value: ts.types.returnHorizon.ReturnHorizon) => void;
  remove?: () => void;
  dailyReporting: boolean;
  inferredWildcard: string;
  context?: Record<string, any>;
};

const SingleReturnHorizon = ({
  rh,
  setRh,
  dailyReporting,
  inferredWildcard,
  remove,
  context,
}: SingleReturnHorizonProps) => {
  const freqDefault = dailyReporting ? 'DAILY' : inferredWildcard;

  const [periods, setPeriods] = React.useState(rh.periods?.amount || 1);
  const [frequency, setFrequency] = React.useState(rh.periods?.frequency || freqDefault);
  const [skipPeriods, setSkipPeriods] = React.useState(rh.skip?.amount || 0);
  const [skipFrequency, setSkipFrequency] = React.useState(rh.skip?.frequency || 'DAILY');

  hooks.useEffectWithoutFirst(() => {
    if (dailyReporting) {
      setSkipFrequency('DAILY');
      setFrequency('DAILY');
    } else {
      setFrequency(inferredWildcard);
      setSkipFrequency(inferredWildcard);
    }
  }, [dailyReporting]);

  const periodsLabel = React.useMemo(() => {
    if (_.isEmpty(context)) return 'period(s)';
    const contextFreq = _.get(context, inferredWildcard.replace('$', ''));
    if (contextFreq) return helpers.returnHorizon._freqToSignature(contextFreq);
    return 'period(s)';
  }, [context]);

  const getLocalSignature = (
    rhPeriods: ts.types.returnHorizon.FrequencyAmount,
    rhSkip: ts.types.returnHorizon.FrequencyAmount
  ) => {
    let label = '';
    if (rhPeriods?.amount && rhPeriods?.frequency)
      if (rhPeriods.frequency == 'DAILY') label += `${rhPeriods.amount} Day Return`;
      else label += `${rhPeriods.amount} Period Return`;
    if (rhSkip?.amount && rhSkip?.frequency)
      if (rhSkip.frequency == 'DAILY') label += `, ${rhSkip.amount} Day Lead`;
      else label += `, ${rhSkip.amount} Period Lead`;

    return label;
  };

  hooks.useEffectWithoutFirst(() => {
    const rhPeriods: ts.types.returnHorizon.FrequencyAmount = {
      amount: periods,
      frequency: frequency as ts.enums.FREQUENCY_ENUM,
    };
    let rhSkip: ts.types.returnHorizon.FrequencyAmount = undefined;

    if (skipPeriods > 0) {
      rhSkip = {
        amount: skipPeriods,
        frequency: skipFrequency as ts.enums.FREQUENCY_ENUM,
      };
    }

    const returnHorizon: ts.types.returnHorizon.ReturnHorizon = {
      periods: rhPeriods,
      skip: rhSkip,
      signature: getLocalSignature(rhPeriods, rhSkip),
    };

    setRh(returnHorizon);
  }, [periods, frequency, skipPeriods, skipFrequency]);

  return (
    <mui.core.Box>
      <mui.core.Grid container spacing={2}>
        <mui.core.Grid size={{ xs: 4 }}>
          <ui.NumericField
            disabled={dailyReporting}
            textFieldProps={{
              label: 'Periods',
              fullWidth: true,
              variant: 'outlined',
              size: 'small',
              required: true,
            }}
            value={periods}
            setValue={(v) => setPeriods(v)}
          />
        </mui.core.Grid>

        <mui.core.Grid size={{ xs: 4 }}>
          <mui.core.TextField
            select
            variant="outlined"
            size="small"
            value={frequency}
            onChange={(e) => setFrequency(e.target.value)}
            fullWidth
            required
            disabled
          >
            {!dailyReporting && <mui.core.MenuItem value={inferredWildcard}>{periodsLabel}</mui.core.MenuItem>}
            <mui.core.MenuItem value="DAILY">day(s)</mui.core.MenuItem>
          </mui.core.TextField>
        </mui.core.Grid>

        {remove && (
          <mui.core.Grid size={{ xs: 2 }} style={{ display: 'flex', alignItems: 'center' }}>
            <mui.core.IconButton onClick={remove} size="small" style={{ opacity: 0.9 }}>
              <mui.icons.Close />
            </mui.core.IconButton>
          </mui.core.Grid>
        )}
      </mui.core.Grid>
      <mui.core.Box mt={2}>
        <mui.core.Grid container spacing={2}>
          <mui.core.Grid size={{ xs: 4 }}>
            <ui.NumericField
              textFieldProps={{
                label: 'Lead',
                fullWidth: true,
                variant: 'outlined',
                size: 'small',
              }}
              value={skipPeriods}
              setValue={(v) => setSkipPeriods(v)}
            />
          </mui.core.Grid>

          <mui.core.Grid size={{ xs: 4 }}>
            <mui.core.TextField
              select
              variant="outlined"
              size="small"
              value={skipFrequency}
              onChange={(e) => setSkipFrequency(e.target.value)}
              fullWidth
            >
              {(dailyReporting ||
                periodsLabel != helpers.returnHorizon._freqToSignature('DAILY' as ts.enums.FREQUENCY_ENUM)) && (
                <mui.core.MenuItem value="DAILY">day(s)</mui.core.MenuItem>
              )}
              {!dailyReporting && <mui.core.MenuItem value={inferredWildcard}>{periodsLabel}</mui.core.MenuItem>}
            </mui.core.TextField>
          </mui.core.Grid>

          {remove && <mui.core.Grid size={{ xs: 2 }} />}
        </mui.core.Grid>
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default SingleReturnHorizon;
